import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';
import { useRedditAuth } from '../RedditAuthContext';
import RhDatePicker from './RhDatePicker';
import RhMonthPicker from './RhMonthPicker';
import SearchableSelect from '../components/SearchableSelect';
import { GUIDE_NICHES, MAX_CLIENT_GUIDE_NICHES } from '../constants/guideNiches';

function rateColor(rate) {
  if (rate >= 40) return '#dc2626';
  if (rate >= 20) return '#ca8a04';
  return '#16a34a';
}

function assignedRedditorsOf(client) {
  if (Array.isArray(client?.assigned_redditors) && client.assigned_redditors.length) {
    return client.assigned_redditors;
  }
  if (client?.assigned_redditor_id) {
    return [{ id: client.assigned_redditor_id, name: client.assigned_redditor_name || 'Redditor' }];
  }
  return [];
}

function assignedNamesLabel(client) {
  const names = assignedRedditorsOf(client).map((r) => r.name).filter(Boolean);
  return names.join(', ');
}

function nameInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

function AssignedRedditorCell({ client }) {
  const assigned = assignedRedditorsOf(client);
  if (!assigned.length) {
    return (
      <span style={{ fontSize: 12, color: 'var(--rh-orange)', fontWeight: 500 }}>
        Unassigned
      </span>
    );
  }
  const namesTip = assigned.map((r) => r.name).filter(Boolean).join('\n');
  return (
    <RhTooltip tip={namesTip}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {assigned.map((r, i) => (
          <span
            key={r.id || `${r.name}-${i}`}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--rh-orange-light)',
              color: 'var(--rh-orange)',
              border: '2px solid #fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              letterSpacing: '0.02em',
              marginLeft: i === 0 ? 0 : -8,
              position: 'relative',
              zIndex: assigned.length - i,
            }}
          >
            {nameInitials(r.name)}
          </span>
        ))}
      </span>
    </RhTooltip>
  );
}

function RhTooltip({ tip, children }) {
  const [coords, setCoords] = useState(null);
  if (!tip) return children;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setCoords({ x: r.left + r.width / 2, y: r.top });
      }}
      onMouseLeave={() => setCoords(null)}
    >
      {children}
      {coords && (
        <span style={{
          position: 'fixed',
          left: coords.x,
          top: coords.y - 8,
          transform: 'translate(-50%, -100%)',
          background: '#1e293b',
          color: '#f8fafc',
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1.45,
          padding: '6px 10px',
          borderRadius: 7,
          whiteSpace: 'pre-wrap',
          maxWidth: 240,
          width: 'max-content',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 99999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {tip}
        </span>
      )}
    </span>
  );
}

function idsEqualOrdered(a, b) {
  if (a.length !== b.length) return false;
  return a.every((id, i) => String(id) === String(b[i]));
}

/* ─── Row action menu (gear → vertical list, portaled to avoid overflow clip) ─ */

function ClientActionMenu({ client, onReassign, onSettings, onArchive }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = 148;
    const menuHeight = 126;
    const pad = 8;
    let top = r.bottom + 4;
    if (top + menuHeight > window.innerHeight - pad) {
      top = Math.max(pad, r.top - menuHeight - 4);
    }
    let left = r.right - menuWidth;
    left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));
    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    placeMenu();
    function onDoc(e) {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onReposition() {
      placeMenu();
    }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, placeMenu]);

  const items = [
    { key: 'reassign', label: 'Reassign', onClick: () => onReassign(client) },
    { key: 'settings', label: 'Settings', onClick: () => onSettings(client) },
    {
      key: 'archive',
      label: client.is_archived ? 'Unarchive' : 'Archive',
      onClick: () => onArchive(client),
      danger: !client.is_archived,
      success: !!client.is_archived,
    },
  ];

  const menu = open && createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        width: 148,
        background: '#fff',
        border: '1px solid var(--rh-border)',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        zIndex: 100000,
        padding: '4px 0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          onClick={() => {
            setOpen(false);
            item.onClick();
          }}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            background: 'none',
            border: 'none',
            padding: '9px 14px',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            color: item.success ? '#15803d' : item.danger ? '#c2410c' : 'var(--rh-text)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: open ? 'var(--rh-orange-light)' : 'none',
          border: `1.5px solid ${open ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
          borderRadius: 7,
          padding: '5px 7px',
          cursor: 'pointer',
          color: open ? 'var(--rh-orange)' : 'var(--rh-text-muted)',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
      </button>
      {menu}
    </>
  );
}

/* ─── Quota Modal (was Cadence Modal) ────────────────────────────────────── */

function CadenceModal({ client, onClose, onSuccess }) {
  const [value, setValue] = useState(client.monthly_cadence ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleSave(e) {
    e.preventDefault();
    const cadence = parseInt(value, 10);
    if (!cadence || cadence < 1) { setError('Please enter a positive number.'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(client.client_name)}/cadence/`,
        { cadence },
      );
      onSuccess(client.client_name, data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update quota. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 440 }}>
        <h3 className="rh-modal-title">Edit Monthly Quota</h3>
        <p className="rh-modal-subtitle">{client.client_name}</p>

        {client.scheduled > 0 && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
            padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1e40af',
          }}>
            This only updates the monthly quota. Existing scheduled comments keep
            their dates — they are never moved automatically.
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="rh-form-group">
            <label>Monthly quota <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="number"
                min="1"
                max="200"
                placeholder="e.g. 20"
                value={value}
                onChange={(e) => { setValue(e.target.value); setError(''); }}
                style={{ flex: 1 }}
                autoFocus
              />
              <span style={{ fontSize: 13, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>
                live comments / month
              </span>
            </div>
            {error && <p className="rh-input-error">{error}</p>}
          </div>

          <div className="rh-modal-actions">
            <button type="button" className="rh-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save Quota'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Reassign Modal ─────────────────────────────────────────────────────── */

function ReassignModal({ client, redditors, onClose, onSuccess }) {
  const current = assignedRedditorsOf(client);
  const [selectedIds, setSelectedIds] = useState(current.map((r) => String(r.id)));
  const [addId, setAddId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedRedditors = selectedIds
    .map((id) => redditors.find((r) => String(r.id) === String(id)))
    .filter(Boolean);
  const availableToAdd = redditors.filter((r) => !selectedIds.includes(String(r.id)));
  const isChanging = !idsEqualOrdered(selectedIds, current.map((r) => String(r.id)));
  const pendingCount = (client.scheduled || 0) + (client.overdue_count || 0);
  const currentName = assignedNamesLabel(client);

  function addRedditor(id) {
    if (!id || selectedIds.includes(id)) return;
    setSelectedIds((prev) => [...prev, id]);
    setAddId('');
  }

  function removeRedditor(id) {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }

  function moveRedditor(id, delta) {
    setSelectedIds((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const next = idx + delta;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });
  }

  async function handleConfirm() {
    if (!selectedIds.length) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(client.client_name)}/assign/`,
        { redditor_ids: selectedIds },
      );
      onSuccess(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reassign. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="rh-modal-overlay" onClick={onClose}>
      <div
        className="rh-modal"
        style={{ maxWidth: 520, width: '94%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rh-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--rh-orange-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" fill="none" stroke="var(--rh-orange)" strokeWidth="2.2" viewBox="0 0 24 24">
                <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </div>
            <div>
              <h3 className="rh-modal-title" style={{ fontSize: 16 }}>Assign Redditors</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                Split pending comments round-robin among the selected redditors
              </p>
            </div>
          </div>
          <button className="rh-modal-close" onClick={onClose}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="rh-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
            borderRadius: 10, padding: '12px 16px',
          }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rh-text-faint)', marginBottom: 3 }}>Client</p>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--rh-text)' }}>{client.client_name}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rh-text-faint)', marginBottom: 3 }}>Pending</p>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: pendingCount > 0 ? '#ca8a04' : 'var(--rh-text-muted)' }}>{pendingCount} comment{pendingCount !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div>
            <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>Currently assigned</p>
            <div style={{
              background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
              borderRadius: 8, padding: '10px 14px', fontSize: 14,
              color: currentName ? 'var(--rh-text)' : 'var(--rh-text-muted)',
            }}>
              {currentName || 'Unassigned'}
            </div>
          </div>

          <div>
            <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>
              Assigned redditors <span style={{ fontWeight: 500, color: 'var(--rh-text-faint)' }}>(order = round-robin)</span>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {selectedRedditors.length === 0 && (
                <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>Select at least one redditor.</p>
              )}
              {selectedRedditors.map((r, idx) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    border: '1px solid var(--rh-border)', borderRadius: 8, padding: '8px 10px',
                    background: '#fff',
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--rh-text-faint)', width: 16 }}>{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{r.name}</span>
                  <button type="button" className="rh-btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => moveRedditor(String(r.id), -1)} disabled={idx === 0}>Up</button>
                  <button type="button" className="rh-btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => moveRedditor(String(r.id), 1)} disabled={idx === selectedRedditors.length - 1}>Down</button>
                  <button type="button" className="rh-btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => removeRedditor(String(r.id))}>Remove</button>
                </div>
              ))}
            </div>
            {availableToAdd.length > 0 && (
              <select
                value={addId}
                onChange={(e) => addRedditor(e.target.value)}
                style={{
                  width: '100%', marginTop: 8, padding: '10px 14px',
                  borderRadius: 8, border: '1.5px solid var(--rh-border)',
                  fontSize: 14, background: '#fff', outline: 'none', cursor: 'pointer',
                }}
              >
                <option value="">+ Add redditor</option>
                {availableToAdd.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
          </div>

          {isChanging && (
            <div style={{
              background: pendingCount > 0 ? 'rgba(255,69,0,0.06)' : 'var(--rh-bg)',
              border: `1px solid ${pendingCount > 0 ? 'rgba(255,69,0,0.2)' : 'var(--rh-border)'}`,
              borderRadius: 10, padding: '12px 16px',
              fontSize: 13, lineHeight: 1.65, color: 'var(--rh-text)',
            }}>
              {pendingCount > 0 ? (
                <>
                  <strong style={{ color: 'var(--rh-orange)' }}>
                    {pendingCount} pending comment{pendingCount !== 1 ? 's' : ''}
                  </strong>
                  {' '}will be split round-robin among{' '}
                  <strong>{selectedRedditors.map((r) => r.name).join(', ')}</strong>.{' '}
                  <span style={{ color: 'var(--rh-text-muted)' }}>Already-submitted comments are not affected.</span>
                </>
              ) : (
                <span style={{ color: 'var(--rh-text-muted)' }}>
                  No pending comments to move — only the assignment record will be updated.
                </span>
              )}
            </div>
          )}

          {error && (
            <p style={{ margin: 0, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
              {error}
            </p>
          )}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="rh-btn-primary"
            onClick={handleConfirm}
            disabled={!selectedIds.length || !isChanging || saving}
          >
            {saving ? 'Saving…' : 'Save assignment'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Rename Modal ───────────────────────────────────────────────────────── */

function RenameClientModal({ client, onClose, onSuccess }) {
  const [newName, setNewName] = useState(client.client_name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // conflict: { old_name, new_name, old_redditor: {id,name}, new_redditor: {id,name} } | null
  const [conflict, setConflict] = useState(null);
  const [chosenRedditorId, setChosenRedditorId] = useState(null);

  const trimmed = newName.trim();
  const changed = trimmed && trimmed !== client.client_name;

  async function handleConfirm() {
    if (!changed) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(client.client_name)}/rename/`,
        { new_name: trimmed },
      );
      onSuccess(data);
    } catch (e) {
      const resp = e.response?.data;
      if (e.response?.status === 409 && resp?.conflict) {
        setConflict(resp);
        setSaving(false);
        return;
      }
      if (e.response?.status === 409 && resp?.merged) {
        onSuccess(resp);
        return;
      }
      setError(resp?.detail || 'Failed to rename. Please try again.');
      setSaving(false);
    }
  }

  async function handleMerge() {
    if (!chosenRedditorId || !conflict) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await redditApi.post(
        `/admin/clients/${encodeURIComponent(conflict.old_name)}/merge/`,
        { target_name: conflict.new_name, redditor_id: chosenRedditorId },
      );
      onSuccess(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Merge failed. Please try again.');
      setSaving(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && changed && !saving && !conflict) handleConfirm();
    if (e.key === 'Escape') onClose();
  }

  const modalIcon = (
    <div style={{
      width: 36, height: 36, borderRadius: 10,
      background: 'var(--rh-orange-light)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <svg width="16" height="16" fill="none" stroke="var(--rh-orange)" strokeWidth="2.2" viewBox="0 0 24 24">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </div>
  );

  const closeBtn = (
    <button className="rh-modal-close" onClick={onClose}>
      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );

  const readBox = {
    background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
    borderRadius: 8, padding: '10px 14px', fontSize: 14,
    fontWeight: 600, color: 'var(--rh-text-muted)',
  };

  return (
    <div className="rh-modal-overlay" onClick={onClose}>
      <div
        className="rh-modal"
        style={{ maxWidth: 440, width: '94%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Step 1: rename form ── */}
        {!conflict && (
          <>
            <div className="rh-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {modalIcon}
                <div>
                  <h3 className="rh-modal-title" style={{ fontSize: 16 }}>Rename Client</h3>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                    Updates the name across all assignments and comments
                  </p>
                </div>
              </div>
              {closeBtn}
            </div>

            <div className="rh-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>Current name</p>
                <div style={readBox}>{client.client_name}</div>
              </div>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>New name</p>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={handleKey}
                  autoFocus
                  style={{
                    width: '100%', padding: '10px 14px',
                    borderRadius: 8, border: `1.5px solid ${changed ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
                    fontSize: 14, fontWeight: 500, color: 'var(--rh-text)',
                    background: '#fff', outline: 'none', boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                />
              </div>
              {error && (
                <p style={{ margin: 0, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
                  {error}
                </p>
              )}
            </div>

            <div className="rh-modal-footer">
              <button className="rh-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="rh-btn-primary" onClick={handleConfirm} disabled={!changed || saving}>
                {saving ? 'Saving…' : 'Save Name'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: redditor conflict picker ── */}
        {conflict && (
          <>
            <div className="rh-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {modalIcon}
                <div>
                  <h3 className="rh-modal-title" style={{ fontSize: 16 }}>Merge Clients</h3>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                    Choose which redditor continues with the merged client
                  </p>
                </div>
              </div>
              {closeBtn}
            </div>

            <div className="rh-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 8, padding: '12px 14px' }}>
                <strong>{conflict.old_name}</strong> and <strong>{conflict.new_name}</strong> have different redditors assigned.
                All comments will be merged into <strong>{conflict.new_name}</strong>.
                Select who should handle the combined workload:
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[conflict.old_redditor, conflict.new_redditor].map((r) => {
                  const selected = chosenRedditorId === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setChosenRedditorId(r.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                        border: `1.5px solid ${selected ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
                        background: selected ? 'var(--rh-orange-light)' : 'var(--rh-surface)',
                        transition: 'all 0.15s', textAlign: 'left',
                      }}
                    >
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: selected ? 'var(--rh-orange)' : 'var(--rh-border)',
                        color: selected ? '#fff' : 'var(--rh-text-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700,
                      }}>
                        {r.name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: selected ? 'var(--rh-orange)' : 'var(--rh-text)' }}>
                        {r.name}
                      </span>
                      {r.id === conflict.new_redditor.id && (
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--rh-text-faint)' }}>current assignee</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {error && (
                <p style={{ margin: 0, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
                  {error}
                </p>
              )}
            </div>

            <div className="rh-modal-footer">
              <button className="rh-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button
                className="rh-btn-primary"
                onClick={handleMerge}
                disabled={!chosenRedditorId || saving}
              >
                {saving ? 'Merging…' : 'Confirm Merge'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Client Settings Modal ──────────────────────────────────────────────── */

function formatSyncedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return null;
  }
}

function DescriptionField({ label, text }) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = (text || '').trim();
  const isLong = trimmed.length > 220;
  const shown = expanded || !isLong ? trimmed : `${trimmed.slice(0, 220)}…`;

  return (
    <div>
      <p style={{ margin: '0 0 6px', fontSize: 11.5, fontWeight: 700, color: 'var(--rh-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </p>
      {trimmed ? (
        <>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--rh-text)', whiteSpace: 'pre-wrap' }}>
            {shown}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, fontSize: 12, fontWeight: 600, color: 'var(--rh-orange)', cursor: 'pointer' }}
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-faint)', fontStyle: 'italic' }}>Not available.</p>
      )}
    </div>
  );
}

function ClientSettingsModal({ client, organizations, onClose, onSuccess, isDeveloper }) {
  const [clientType, setClientType] = useState(client.client_type || 'billable');
  const [orgId, setOrgId] = useState(client.organization_id || '');
  const [niches, setNiches] = useState(
    Array.isArray(client.niches)
      ? client.niches.filter((n) => GUIDE_NICHES.includes(n)).slice(0, MAX_CLIENT_GUIDE_NICHES)
      : [],
  );
  const [nichesSource, setNichesSource] = useState(client.niches_source || 'ai');
  const [nocodb, setNocodb] = useState({
    one_sentence_description: client.one_sentence_description || '',
    core_offering: client.core_offering || '',
    website: client.website || '',
    nocodb_synced_at: client.nocodb_synced_at || null,
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [syncMsg, setSyncMsg] = useState('');

  function toggleNiche(tag) {
    setNiches((prev) => {
      if (prev.includes(tag)) return prev.filter((n) => n !== tag);
      if (prev.length >= MAX_CLIENT_GUIDE_NICHES) return prev;
      return [...prev, tag];
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      // Only include client_type if the current user is a developer
      ...(isDeveloper ? { client_type: clientType } : {}),
      organization_id: orgId || null,
      niches,
    };
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(client.client_name)}/settings/`,
        payload,
      );
      if (data.niches) setNiches(data.niches);
      if (data.niches_source) setNichesSource(data.niches_source);
      onSuccess(client.client_name, data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSyncNocodb() {
    setSyncing(true);
    setSyncMsg('');
    setError('');
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(client.client_name)}/settings/`,
        { resync_nocodb: true },
      );
      setNocodb({
        one_sentence_description: data.one_sentence_description || '',
        core_offering: data.core_offering || '',
        website: data.website || '',
        nocodb_synced_at: data.nocodb_synced_at || null,
      });
      if (Array.isArray(data.niches)) setNiches(data.niches);
      if (data.niches_source) setNichesSource(data.niches_source);
      onSuccess(client.client_name, data);
      setSyncMsg(data.one_sentence_description || data.core_offering ? 'Synced from NocoDB.' : 'No matching client found in NocoDB.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sync from NocoDB. Try again.');
    } finally {
      setSyncing(false);
    }
  }

  const typeLabel = clientType === 'value_add' ? 'Value-Add (180 Marketing)' : 'Billable (Visibility Labs)';
  const syncedLabel = formatSyncedAt(nocodb.nocodb_synced_at);

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 480, width: '94%' }} onClick={(e) => e.stopPropagation()}>
        <div className="rh-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--rh-orange-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" fill="none" stroke="var(--rh-orange)" strokeWidth="2.2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
            <div>
              <h3 className="rh-modal-title" style={{ fontSize: 16 }}>Client Settings</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>{client.client_name}</p>
            </div>
          </div>
          <button className="rh-modal-close" onClick={onClose} disabled={saving}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div className="rh-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Client Type — developer-only */}
            {isDeveloper ? (
              <div>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Client Type <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(developer only)</span>
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { value: 'billable', label: 'Billable', sub: 'Visibility Labs', color: '#7c3aed' },
                    { value: 'value_add', label: 'Value-Add', sub: '180 Marketing', color: '#0369a1' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setClientType(opt.value)}
                      style={{
                        flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${clientType === opt.value ? opt.color : 'var(--rh-border)'}`,
                        background: clientType === opt.value ? `${opt.color}10` : 'var(--rh-surface)',
                        transition: 'all 0.15s', textAlign: 'left',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: clientType === opt.value ? opt.color : 'var(--rh-text)' }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: clientType === opt.value ? opt.color : 'var(--rh-text-muted)', marginTop: 2 }}>{opt.sub}</div>
                    </button>
                  ))}
                </div>
                {clientType === 'value_add' && (
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 6, padding: '6px 10px' }}>
                    Value-Add clients do not receive replacement comments when comments are deleted.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Client Type
                </p>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-faint)', fontStyle: 'italic' }}>
                  {client.client_type === 'value_add' ? 'Value-Add (180 Marketing)' : client.client_type === 'billable' ? 'Billable (Visibility Labs)' : '—'}
                  {' '}— set automatically from organization.
                </p>
              </div>
            )}

            {/* Organization */}
            <div>
              <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Organization
              </p>
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px',
                  borderRadius: 8, border: `1.5px solid ${orgId ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
                  fontSize: 14, color: orgId ? 'var(--rh-text)' : 'var(--rh-text-muted)',
                  background: '#fff', outline: 'none', cursor: 'pointer',
                  appearance: 'auto',
                }}
              >
                <option value="">— Unassigned —</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            {/* Target niches — from the 20 onboarding guide categories */}
            <div style={{
              border: '1px solid var(--rh-border)', borderRadius: 10,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12,
              background: 'var(--rh-bg)',
            }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Target Niches
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'var(--rh-text-faint)' }}>
                  Pick up to {MAX_CLIENT_GUIDE_NICHES} from the Redditor onboarding guide
                  {nichesSource === 'admin' ? ' (manually edited — sync will not overwrite).' : ' (AI-filled from NocoDB until you edit).'}
                </p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {GUIDE_NICHES.map((tag) => {
                  const selected = niches.includes(tag);
                  const atCap = !selected && niches.length >= MAX_CLIENT_GUIDE_NICHES;
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleNiche(tag)}
                      disabled={atCap}
                      title={atCap ? `Maximum ${MAX_CLIENT_GUIDE_NICHES} niches` : tag}
                      style={{
                        fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 16,
                        cursor: atCap ? 'not-allowed' : 'pointer',
                        opacity: atCap ? 0.45 : 1,
                        border: selected ? '1px solid var(--rh-orange)' : '1px solid var(--rh-border)',
                        background: selected ? 'var(--rh-orange-light, #fff3ed)' : '#fff',
                        color: selected ? 'var(--rh-orange)' : 'var(--rh-text-muted)',
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--rh-text-muted)' }}>
                {niches.length}/{MAX_CLIENT_GUIDE_NICHES} selected
              </p>
            </div>

            {/* NocoDB data — read-only source for AI niche extraction */}
            <div style={{
              border: '1px solid var(--rh-border)', borderRadius: 10,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14,
              background: 'var(--rh-bg)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--rh-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    NocoDB Data
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'var(--rh-text-faint)' }}>
                    {syncedLabel ? `Last synced: ${syncedLabel}` : 'Not yet synced.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSyncNocodb}
                  disabled={syncing}
                  style={{
                    background: '#fff', border: '1.5px solid var(--rh-orange)',
                    borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700,
                    color: 'var(--rh-orange)', cursor: syncing ? 'default' : 'pointer',
                    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
                    opacity: syncing ? 0.6 : 1,
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  {syncing ? 'Syncing…' : 'Sync from NocoDB'}
                </button>
              </div>

              {syncMsg && (
                <p style={{ margin: 0, fontSize: 12, color: syncMsg.startsWith('No') ? '#b45309' : '#16a34a', fontWeight: 600 }}>
                  {syncMsg}
                </p>
              )}

              {nocodb.website && (
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 11.5, fontWeight: 700, color: 'var(--rh-text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Website
                  </p>
                  <a
                    href={nocodb.website.startsWith('http') ? nocodb.website : `https://${nocodb.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13, color: '#0369a1', wordBreak: 'break-all' }}
                  >
                    {nocodb.website}
                  </a>
                </div>
              )}

              <DescriptionField label="1-Sentence Description" text={nocodb.one_sentence_description} />
              <DescriptionField label="Core Offering" text={nocodb.core_offering} />

              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--rh-text-muted)' }}>
                Pulled from NocoDB. Used to AI-generate target niches when niches are not manually
                edited. Edit source data in NocoDB, then sync.
              </p>
            </div>

            {error && (
              <p style={{ margin: 0, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
                {error}
              </p>
            )}
          </div>

          <div className="rh-modal-footer">
            <button type="button" className="rh-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/* ─── Bulk Assign Org Modal ──────────────────────────────────────────────── */

function BulkAssignOrgModal({ organizations, unassignedOrgCount, onClose, onSuccess }) {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleAssign() {
    if (!selectedOrgId) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await redditApi.post('/admin/clients/bulk-assign-org/', {
        organization_id: selectedOrgId,
        client_names: ['*'],
      });
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Bulk assign failed. Try again.');
      setSaving(false);
    }
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

  return (
    <div className="rh-modal-overlay" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 440, width: '94%' }} onClick={(e) => e.stopPropagation()}>
        <div className="rh-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--rh-orange-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" fill="none" stroke="var(--rh-orange)" strokeWidth="2.2" viewBox="0 0 24 24">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
            </div>
            <div>
              <h3 className="rh-modal-title" style={{ fontSize: 16 }}>Bulk Assign Organization</h3>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                Assign all {unassignedOrgCount} unassigned client{unassignedOrgCount !== 1 ? 's' : ''} to an organization
              </p>
            </div>
          </div>
          <button className="rh-modal-close" onClick={onClose} disabled={saving}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="rh-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.6, background: 'var(--rh-surface)', border: '1px solid var(--rh-border)', borderRadius: 8, padding: '12px 14px' }}>
            This will assign <strong>{unassignedOrgCount} client{unassignedOrgCount !== 1 ? 's' : ''}</strong> that currently have no organization to the selected org. Clients already assigned to an org will not be changed.
          </div>

          <div>
            <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>Select Organization</p>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px',
                borderRadius: 8, border: `1.5px solid ${selectedOrgId ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
                fontSize: 14, color: selectedOrgId ? 'var(--rh-text)' : 'var(--rh-text-muted)',
                background: '#fff', outline: 'none', cursor: 'pointer', appearance: 'auto',
              }}
            >
              <option value="">— Select an organization —</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <p style={{ margin: 0, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
              {error}
            </p>
          )}
        </div>

        <div className="rh-modal-footer">
          <button className="rh-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="rh-btn-primary"
            onClick={handleAssign}
            disabled={!selectedOrgId || saving}
          >
            {saving ? 'Assigning…' : `Assign to ${selectedOrg ? selectedOrg.name : '…'}`}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─── Vertical column header (matches AccountPerformanceTab style) ────────── */

function VerticalTh({ label, title, style = {}, className }) {
  return (
    <th
      className={className}
      title={title || label}
      style={{
        width: 42, minWidth: 42, padding: '8px 4px',
        verticalAlign: 'bottom', textAlign: 'center',
        ...style,
      }}
    >
      {/* Flex column with alignItems:center reliably centers vertical text */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{
          writingMode: 'vertical-lr',
          transform: 'rotate(180deg)',
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
      </div>
    </th>
  );
}


/* ─── Main tab ───────────────────────────────────────────────────────────── */

function currentMonthStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function ClientAssignmentsTab({ refreshTrigger = 0, onClientsChanged }) {
  const { user } = useRedditAuth();
  const isDeveloper = !!(user?.is_developer);

  const [clients, setClients] = useState([]);
  const [redditors, setRedditors] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassigning, setReassigning] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [editingCadence, setEditingCadence] = useState(null);
  const [editingSettings, setEditingSettings] = useState(null);
  const [bulkAssignOrg, setBulkAssignOrg] = useState(false);
  const [toast, setToast] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | archived | all
  const [archiveTarget, setArchiveTarget] = useState(null); // client | null
  const [archiveBusy, setArchiveBusy] = useState(false);

  // Month filter — defaults to current calendar month so quota progress is always visible
  const [monthFilter, setMonthFilter] = useState(currentMonthStr());
  // Custom date range overrides the month picker when set
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Client name search (client-side filter on loaded data)
  const [clientSearch, setClientSearch] = useState('');
  // Redditor filter (client-side filter on loaded data)
  const [redditorFilter, setRedditorFilter] = useState('');

  // A date context is active when month or custom range is set
  const hasMonthFilter = !!monthFilter && !dateFrom && !dateTo;
  const hasCustomRange = !!(dateFrom || dateTo);
  const isFiltered = hasMonthFilter || hasCustomRange;

  const statusOptions = [
    { id: 'active', label: 'Active' },
    { id: 'archived', label: 'Archived' },
    { id: 'all', label: 'All' },
  ];

  const showToast = useCallback((msg, ms = 4000) => {
    setToast(msg);
    setTimeout(() => setToast(''), ms);
  }, []);

  const load = useCallback(async (month, from, to, status) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (month && !from && !to) {
        params.set('month', month);
      } else {
        if (from) params.set('date_from', from);
        if (to) params.set('date_to', to);
      }
      if (status && status !== 'active') {
        params.set('status', status);
      } else if (status === 'active') {
        params.set('status', 'active');
      }
      const qs = params.toString() ? `?${params}` : '';
      const [clientsRes, orgsRes] = await Promise.all([
        redditApi.get(`/admin/clients/${qs}`),
        redditApi.get('/admin/organizations/'),
      ]);
      setClients(clientsRes.data.clients || []);
      setRedditors(clientsRes.data.redditors || []);
      setOrganizations(orgsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(monthFilter, dateFrom, dateTo, statusFilter); }, [load, monthFilter, dateFrom, dateTo, statusFilter]);
  // Re-fetch whenever a dev action signals a global data change.
  useEffect(() => { if (refreshTrigger > 0) load(monthFilter, dateFrom, dateTo, statusFilter); }, [refreshTrigger]); // eslint-disable-line

  // Client-side search + redditor filters applied on top of server results
  const displayedClients = clients
    .filter((c) => {
      if (clientSearch.trim() && !c.client_name.toLowerCase().includes(clientSearch.trim().toLowerCase())) return false;
      if (redditorFilter) {
        const assigned = assignedRedditorsOf(c);
        if (redditorFilter === '__unassigned__' && assigned.length) return false;
        if (redditorFilter !== '__unassigned__' && !assigned.some((r) => String(r.id) === String(redditorFilter))) return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => a.client_name.localeCompare(b.client_name, undefined, { sensitivity: 'base' }));

  function handleRenameSuccess(data) {
    if (data.merged) {
      // A merge happened — reload to get accurate totals for the surviving client
      load(monthFilter, dateFrom, dateTo, statusFilter);
      showToast(`Merged into "${data.new_name}" successfully.`);
    } else {
      setClients((prev) =>
        prev.map((c) =>
          c.client_name === data.old_name ? { ...c, client_name: data.new_name } : c,
        ),
      );
      showToast(`Client renamed to "${data.new_name}".`);
    }
    setRenaming(null);
  }

  async function confirmArchiveClient() {
    if (!archiveTarget) return;
    const archive = !archiveTarget.is_archived;
    const name = archiveTarget.client_name;
    setArchiveBusy(true);
    try {
      const { data } = await redditApi.post(
        `/admin/clients/${encodeURIComponent(name)}/archive/`,
        { archived: archive },
      );
      setArchiveTarget(null);
      showToast(
        archive
          ? `"${data.client_name}" archived. Hidden from Active lists, sidebar, redditor dashboards, and share links.`
          : `"${data.client_name}" restored to Active.`,
      );
      // Reload so status filter removes/adds the row correctly
      load(monthFilter, dateFrom, dateTo, statusFilter);
      // Refresh sidebar Client dropdown (active-only)
      onClientsChanged?.(archive ? { archivedClientName: name } : {});
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update archive status.');
    } finally {
      setArchiveBusy(false);
    }
  }

  function handleReassignSuccess(data) {
    setClients((prev) =>
      prev.map((c) =>
        c.client_name === data.client_name
          ? {
              ...c,
              assigned_redditors: data.assigned_redditors || [],
              assigned_redditor_id: data.assigned_redditor_id,
              assigned_redditor_name: data.assigned_redditor_name,
            }
          : c,
      ),
    );
    const moved = data.reassigned_comments;
    showToast(
      moved > 0
        ? `Assigned to ${data.assigned_redditor_name}. ${moved} pending comment${moved !== 1 ? 's' : ''} redistributed.`
        : `Assigned to ${data.assigned_redditor_name}.`,
    );
    setReassigning(null);
  }

  function handleCadenceSuccess(clientName, data) {
    setClients((prev) =>
      prev.map((c) =>
        c.client_name === clientName
          ? { ...c, monthly_cadence: data.monthly_cadence }
          : c,
      ),
    );
    showToast(`Quota set to ${data.monthly_cadence}/mo.`, 5000);
    setEditingCadence(null);
  }

  function handleSettingsSuccess(clientName, data) {
    const orgName = organizations.find((o) => o.id === data.organization_id)?.name || data.organization_name || null;
    setClients((prev) =>
      prev.map((c) =>
        c.client_name === clientName
          ? {
              ...c,
              client_type: data.client_type,
              organization_id: data.organization_id,
              organization_name: orgName,
              ...(data.one_sentence_description !== undefined ? { one_sentence_description: data.one_sentence_description } : {}),
              ...(data.core_offering !== undefined ? { core_offering: data.core_offering } : {}),
              ...(data.website !== undefined ? { website: data.website } : {}),
              ...(data.niches !== undefined ? { niches: data.niches } : {}),
              ...(data.niches_source !== undefined ? { niches_source: data.niches_source } : {}),
              ...(data.nocodb_synced_at !== undefined ? { nocodb_synced_at: data.nocodb_synced_at } : {}),
            }
          : c,
      ),
    );
    showToast(`Settings saved for ${clientName}.`);
  }

  function handleBulkAssignSuccess(data) {
    // Reload to reflect all the newly assigned orgs
    load(monthFilter, dateFrom, dateTo, statusFilter);
    showToast(data.message || `Clients assigned to org.`);
    setBulkAssignOrg(false);
  }

  async function handleInlineOrgChange(clientName, orgId) {
    try {
      const { data } = await redditApi.patch(
        `/admin/clients/${encodeURIComponent(clientName)}/settings/`,
        { organization_id: orgId || null },
      );
      const orgName = organizations.find((o) => o.id === data.organization_id)?.name || data.organization_name || null;
      setClients((prev) =>
        prev.map((c) =>
          c.client_name === clientName
            ? {
                ...c,
                organization_id: data.organization_id,
                organization_name: orgName,
                // Backend auto-derives client_type from org — reflect it here
                client_type: data.client_type,
              }
            : c,
        ),
      );
    } catch (err) {
      showToast('Failed to update organization.');
    }
  }

  const unassigned = clients.filter((c) => assignedRedditorsOf(c).length === 0).length;
  const unassignedOrg = clients.filter((c) => !c.organization_id).length;

  // Human-readable period label for the filter bar
  const periodLabel = hasMonthFilter
    ? new Date(monthFilter + '-02').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : hasCustomRange
      ? 'Custom range'
      : 'All time';

  return (
    <>
      <div className="rh-tab-scroll rh-clients-tab">
      <div className="rh-page-header">
        <div>
          <h2 className="rh-page-title">Client Assignments</h2>
          <p className="rh-page-subtitle">
            Clients can be assigned to one or more redditors. Pending comments are split round-robin in assignment order.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{
            display: 'inline-flex', background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
            borderRadius: 9, padding: 3, gap: 2,
          }}>
            {statusOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  background: statusFilter === opt.id ? 'var(--rh-orange)' : 'transparent',
                  color: statusFilter === opt.id ? '#fff' : 'var(--rh-text-muted)',
                  transition: 'background 0.2s ease, color 0.2s ease',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {!loading && unassignedOrg > 0 && (
            <button
              onClick={() => setBulkAssignOrg(true)}
              style={{
                background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: 10, padding: '8px 14px',
                fontSize: 13, color: '#1d4ed8', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              {unassignedOrg} without org
            </button>
          )}
          {!loading && unassigned > 0 && (
            <div style={{
              background: 'var(--rh-orange-light)',
              border: '1px solid var(--rh-orange-border)',
              borderRadius: 10, padding: '10px 16px',
              fontSize: 13, color: 'var(--rh-orange)', fontWeight: 600,
            }}>
              {unassigned} client{unassigned !== 1 ? 's' : ''} unassigned
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: '#fff', border: '1px solid var(--rh-border)',
          borderLeft: '4px solid var(--rh-orange)',
          borderRadius: 10, padding: '12px 18px',
          boxShadow: 'var(--rh-shadow-md)',
          fontSize: 13, color: 'var(--rh-text)',
          maxWidth: 360,
        }}>
          {toast}
        </div>
      )}

      {/* Filter bar: Month picker + Client search + Custom date range */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        marginBottom: 16, padding: '12px 16px',
        background: isFiltered ? 'var(--rh-orange-light)' : '#f9fafb',
        border: `1px solid ${isFiltered ? 'var(--rh-orange-border)' : 'var(--rh-border)'}`,
        borderRadius: 10,
      }}>
        <svg width="15" height="15" fill="none" stroke={isFiltered ? 'var(--rh-orange)' : 'var(--rh-text-muted)'} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: isFiltered ? 'var(--rh-orange)' : 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>
          {periodLabel}
        </span>

        {/* Month picker — primary quota filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>Month</label>
          <RhMonthPicker
            value={monthFilter}
            onChange={(v) => { setMonthFilter(v); setDateFrom(''); setDateTo(''); }}
            placeholder="Pick month"
          />
        </div>

        {/* Client search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>Client</label>
          <SearchableSelect
            options={[...clients]
              .sort((a, b) => a.client_name.localeCompare(b.client_name, undefined, { sensitivity: 'base' }))
              .map((c) => ({ value: c.client_name, label: c.client_name }))}
            value={clientSearch}
            onChange={setClientSearch}
            placeholder="All clients"
            allOption={{ value: '', label: 'All clients' }}
            width={170}
          />
        </div>

        {/* Redditor filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>Redditor</label>
          <SearchableSelect
            options={[
              ...redditors.map((r) => ({ value: r.id, label: r.name })),
              { value: '__unassigned__', label: '— Unassigned —' },
            ]}
            value={redditorFilter}
            onChange={setRedditorFilter}
            placeholder="All redditors"
            allOption={{ value: '', label: 'All redditors' }}
            width={170}
          />
        </div>

        {/* Custom date range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>From</label>
          <RhDatePicker value={dateFrom} onChange={(v) => { setDateFrom(v); if (v) setMonthFilter(''); }} placeholder="From date" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap' }}>To</label>
          <RhDatePicker value={dateTo} onChange={(v) => { setDateTo(v); if (v) setMonthFilter(''); }} placeholder="To date" />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {(clientSearch || redditorFilter) && (
            <button
              onClick={() => { setClientSearch(''); setRedditorFilter(''); }}
              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: 12, color: 'var(--rh-text-muted)', cursor: 'pointer', fontWeight: 600 }}
            >
              Clear filters
            </button>
          )}
          {hasCustomRange && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); setMonthFilter(currentMonthStr()); }}
              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: 12, color: 'var(--rh-orange)', cursor: 'pointer', fontWeight: 600 }}
            >
              Reset to this month
            </button>
          )}
          {hasMonthFilter && monthFilter !== currentMonthStr() && (
            <button
              onClick={() => setMonthFilter(currentMonthStr())}
              style={{ background: 'none', border: 'none', padding: '4px 8px', fontSize: 12, color: 'var(--rh-orange)', cursor: 'pointer', fontWeight: 600 }}
            >
              Jump to current month
            </button>
          )}
        </div>
      </div>

      <div className="rh-card rh-clients-card">
        <div className="rh-card-header">
          <h3 className="rh-card-title">All Clients</h3>
          <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>
            {loading ? '…' : `${displayedClients.length} client${displayedClients.length !== 1 ? 's' : ''}${(clientSearch || redditorFilter) ? ` (filtered from ${clients.length})` : ''}`}
          </span>
        </div>
        <div className="rh-table-wrap rh-clients-table-wrap">
          <table className="rh-table rh-clients-table">
            <thead>
              <tr>
                <th style={{ minWidth: 120, maxWidth: 160 }}>Client</th>
                <th style={{ minWidth: 128, width: 148, textAlign: 'center' }}>Assigned Redditor</th>
                <VerticalTh label="Total" />
                <VerticalTh label="Scheduled" title="Scheduled (not overdue)" />
                <VerticalTh label="Overdue" style={{ color: '#ea580c' }} />
                <VerticalTh label="Submitted" />
                <VerticalTh label="Live" style={{ color: '#16a34a' }} />
                <VerticalTh label="Deleted" />
                <VerticalTh label="Inst. Del." title="Instantly Deleted (DWS)" />
                <VerticalTh label="Archived" />
                <VerticalTh label="Del. Rate" title="Deletion Rate" />
                <VerticalTh label="Quota" style={{ minWidth: 64, width: 64 }} />
                {isFiltered && <VerticalTh label="Remaining" />}
                {isFiltered && <VerticalTh label="Est. Cost" title="Estimated monthly cost to fully replace missing quota (assumes every comment is paid at $5)" />}
                {isFiltered && <th style={{ minWidth: 120, maxWidth: 140, whiteSpace: 'nowrap', textAlign: 'center', padding: '8px 8px' }}>Quota Progress</th>}
                <th style={{ whiteSpace: 'nowrap', width: '1px', textAlign: 'center', padding: '8px 8px' }}>Last Publish Date</th>
                <th style={{ textAlign: 'center', whiteSpace: 'nowrap', width: '1px', padding: '8px 6px' }}>Type</th>
                <VerticalTh label="Action" className="rh-sticky-action" style={{ width: 44, minWidth: 44 }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isFiltered ? 18 : 15} className="rh-empty">Loading…</td></tr>
              ) : displayedClients.length === 0 ? (
                <tr>
                  <td colSpan={isFiltered ? 18 : 15} className="rh-empty">
                    {clientSearch || redditorFilter
                      ? `No clients match the current filters.`
                      : 'No clients yet. Upload a CSV or send comments via the webhook to get started.'}
                  </td>
                </tr>
              ) : displayedClients.map((c) => (
                <tr key={c.client_name}>
                  {/* Client name + rename */}
                  <td style={{ maxWidth: 160 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                      <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.client_name}</span>
                      {c.is_archived && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                          color: '#9a3412', background: '#ffedd5', borderRadius: 999,
                          padding: '1px 7px', flexShrink: 0,
                        }}>
                          Archived
                        </span>
                      )}
                      <button
                        onClick={() => setRenaming(c)}
                        title="Rename client"
                        style={{
                          background: 'none', border: 'none', padding: '2px 4px',
                          cursor: 'pointer', color: 'var(--rh-text-faint)',
                          display: 'inline-flex', alignItems: 'center',
                          borderRadius: 4, transition: 'color 0.15s', lineHeight: 1,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--rh-orange)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--rh-text-faint)'; }}
                      >
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </span>
                  </td>

                  {/* Assigned redditor — initials only; names on instant hover */}
                  <td style={{ textAlign: 'center' }}>
                    <AssignedRedditorCell client={c} />
                  </td>

                  <td style={{ color: 'var(--rh-text-muted)', fontSize: 13, textAlign: 'center' }}>{c.total}</td>
                  <td style={{ color: '#ca8a04', fontSize: 13, textAlign: 'center' }}>{c.scheduled}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      fontSize: 13, fontWeight: (c.overdue_count ?? 0) > 0 ? 700 : 400,
                      color: (c.overdue_count ?? 0) > 0 ? '#ea580c' : 'var(--rh-text-faint)',
                    }}>
                      {c.overdue_count ?? 0}
                    </span>
                  </td>
                  <td style={{ color: 'var(--rh-text-muted)', fontSize: 13, textAlign: 'center' }}>{c.submitted}</td>

                  {/* Live — primary quota numerator */}
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#16a34a' }}>
                      {c.live_count ?? 0}
                    </span>
                  </td>

                  {/* Deleted (DELETED only, not DWS) */}
                  <td style={{ color: '#dc2626', fontSize: 13, textAlign: 'center' }}>
                    {(c.deleted ?? 0) - (c.instant_deleted_count ?? 0)}
                  </td>

                  {/* Instant Deleted (DWS) */}
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 13, color: c.instant_deleted_count > 0 ? '#ea580c' : 'var(--rh-text-faint)' }}>
                      {c.instant_deleted_count ?? 0}
                    </span>
                  </td>

                  {/* Archived */}
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 13, color: c.archived_count > 0 ? '#6b7280' : 'var(--rh-text-faint)' }}>
                      {c.archived_count ?? 0}
                    </span>
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: rateColor(c.deletion_rate) }}>
                      {c.deletion_rate}%
                    </span>
                  </td>

                  {/* Quota (was Cadence) + replacement info */}
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: c.monthly_cadence ? 600 : 400, color: c.monthly_cadence ? 'var(--rh-text)' : 'var(--rh-text-faint)', whiteSpace: 'nowrap' }}>
                          {c.monthly_cadence ? `${c.monthly_cadence} / mo` : '—'}
                        </span>
                        {c.client_type === 'billable' && c.monthly_cadence && (
                          <span style={{ fontSize: 10, color: 'var(--rh-text-faint)', whiteSpace: 'nowrap' }}>
                            {c.replacements_this_month ?? 0}/{c.max_replacements_per_month ?? c.monthly_cadence} repl.
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => setEditingCadence(c)}
                        title="Edit quota"
                        style={{
                          background: 'none', border: 'none', padding: '2px 4px',
                          cursor: 'pointer', color: 'var(--rh-text-faint)',
                          display: 'inline-flex', alignItems: 'center',
                          borderRadius: 4, transition: 'color 0.15s', lineHeight: 1,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--rh-orange)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--rh-text-faint)'; }}
                      >
                        <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    </span>
                  </td>

                  {/* Remaining — shown when a date/month filter is active */}
                  {isFiltered && (
                    <td style={{ textAlign: 'center' }}>
                      {c.monthly_cadence != null ? (() => {
                        const rem = c.remaining ?? Math.max(0, c.monthly_cadence - (c.live_count ?? 0));
                        return (
                          <span style={{
                            fontWeight: 700, fontSize: 13,
                            color: rem === 0 ? '#16a34a' : rem <= 3 ? '#ca8a04' : '#dc2626',
                          }}>
                            {rem === 0 ? '✓ 0' : rem}
                          </span>
                        );
                      })() : (
                        <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>—</span>
                      )}
                    </td>
                  )}

                  {/* Est. Cost — estimated cost to fully replace missing quota (comments still needed + comments needing replacement, @ $5 each) */}
                  {isFiltered && (
                    <td style={{ textAlign: 'center' }}>
                      {c.monthly_cadence != null ? (() => {
                        const missingInitial = Math.max(0, c.monthly_cadence - (c.total ?? 0));
                        const needsReplacement = (c.total ?? 0) - (c.live_count ?? 0);
                        const cost = (missingInitial + needsReplacement) * 5;
                        return (
                          <span
                            style={{ fontWeight: 600, fontSize: 13, color: 'var(--rh-text)' }}
                            title={`${missingInitial} to add + ${needsReplacement} to replace`}
                          >
                            ${cost.toLocaleString()}
                          </span>
                        );
                      })() : (
                        <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>—</span>
                      )}
                    </td>
                  )}

                  {/* Quota Progress — shown when a date/month filter is active */}
                  {isFiltered && (
                    <td style={{ minWidth: 120, maxWidth: 140, textAlign: 'center', padding: '8px 8px' }}>
                      {c.monthly_cadence ? (() => {
                        const liveCount = c.live_count ?? 0;
                        const quota = c.monthly_cadence;
                        const pct = Math.min(100, Math.round((liveCount / quota) * 100));
                        const color = pct >= 100 ? '#16a34a' : pct >= 60 ? '#ca8a04' : '#dc2626';
                        const isOver = liveCount > quota;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, minWidth: 48, background: '#f3f4f6', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {liveCount} / {quota}
                              </span>
                            </div>
                            <span style={{ fontSize: 10, color: pct >= 100 ? '#16a34a' : 'var(--rh-text-faint)' }}>
                              {isOver ? 'Exceeded quota' : pct >= 100 ? 'Quota met' : `${pct}% — live`}
                            </span>
                          </div>
                        );
                      })() : (
                        <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>No quota set</span>
                      )}
                    </td>
                  )}

                  {/* Last Publish Date */}
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap', textAlign: 'center', padding: '8px 8px', color: c.last_scheduled_date ? 'var(--rh-text)' : 'var(--rh-text-faint)' }}>
                    {c.last_scheduled_date
                      ? new Date(c.last_scheduled_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>

                  {/* Type badge — moved to second-last */}
                  <td style={{ textAlign: 'center' }}>
                    {c.client_type ? (
                      <span style={{
                        display: 'inline-block',
                        fontSize: 11, fontWeight: 700, padding: '3px 8px',
                        borderRadius: 20,
                        background: c.client_type === 'value_add' ? '#dbeafe' : '#f3e8ff',
                        color: c.client_type === 'value_add' ? '#1d4ed8' : '#7c3aed',
                        whiteSpace: 'nowrap',
                      }}>
                        {c.client_type === 'value_add' ? 'Value-Add' : 'Billable'}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                    )}
                  </td>

                  {/* Actions — gear menu (Reassign / Settings / Archive) */}
                  <td className="rh-sticky-action" style={{ textAlign: 'center', padding: '6px 8px', width: 44 }}>
                    <ClientActionMenu
                      client={c}
                      onReassign={setReassigning}
                      onSettings={setEditingSettings}
                      onArchive={setArchiveTarget}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {reassigning && (
        <ReassignModal
          client={reassigning}
          redditors={redditors}
          onClose={() => setReassigning(null)}
          onSuccess={handleReassignSuccess}
        />
      )}

      {archiveTarget && (
        <div
          className="rh-modal-overlay"
          onClick={(e) => {
            if (!archiveBusy && e.target === e.currentTarget) setArchiveTarget(null);
          }}
        >
          <div className="rh-modal" style={{ maxWidth: 420, width: '92%' }}>
            <div className="rh-modal-header" style={{ padding: '14px 18px' }}>
              <h3 className="rh-modal-title" style={{ fontSize: 15 }}>
                {archiveTarget.is_archived ? 'Unarchive Client' : 'Archive Client'}
              </h3>
              <button
                className="rh-modal-close"
                onClick={() => setArchiveTarget(null)}
                disabled={archiveBusy}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rh-modal-body" style={{ padding: '14px 18px' }}>
              {archiveTarget.is_archived ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.55 }}>
                  Restore <strong>{archiveTarget.client_name}</strong> to Active? It will show again
                  in the client list, sidebar, and on the assigned redditor’s dashboard.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.55 }}>
                  Archive <strong>{archiveTarget.client_name}</strong>? It will be hidden from Active
                  lists and the assigned redditor’s dashboard. Comments are kept; you can unarchive later.
                </p>
              )}
            </div>
            <div className="rh-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px' }}>
              <button
                className="rh-btn-secondary"
                onClick={() => setArchiveTarget(null)}
                disabled={archiveBusy}
              >
                Cancel
              </button>
              <button
                className="rh-btn-primary"
                onClick={confirmArchiveClient}
                disabled={archiveBusy}
                style={{
                  padding: '9px 16px', fontSize: 13,
                  background: archiveTarget.is_archived ? '#16a34a' : undefined,
                }}
              >
                {archiveBusy
                  ? (archiveTarget.is_archived ? 'Restoring…' : 'Archiving…')
                  : (archiveTarget.is_archived ? 'Unarchive' : 'Archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {renaming && (
        <RenameClientModal
          client={renaming}
          onClose={() => setRenaming(null)}
          onSuccess={handleRenameSuccess}
        />
      )}

      {editingCadence && (
        <CadenceModal
          client={editingCadence}
          onClose={() => setEditingCadence(null)}
          onSuccess={handleCadenceSuccess}
        />
      )}

      {editingSettings && (
        <ClientSettingsModal
          client={editingSettings}
          organizations={organizations}
          onClose={() => setEditingSettings(null)}
          onSuccess={handleSettingsSuccess}
          isDeveloper={isDeveloper}
        />
      )}

      {bulkAssignOrg && (
        <BulkAssignOrgModal
          organizations={organizations}
          unassignedOrgCount={unassignedOrg}
          onClose={() => setBulkAssignOrg(false)}
          onSuccess={handleBulkAssignSuccess}
        />
      )}
      </div>{/* end rh-tab-scroll */}
    </>
  );
}
